import { HomeBlockId, homeBlockDefinition } from './home-blocks';

/**
 * Linhas da Home a partir dos blocos que vão aparecer agora, na ordem (card 3788).
 *
 * - Bloco "metade" divide a linha com o próximo bloco se ele também for "metade".
 * - Bloco "metade" sem par ocupa a linha inteira.
 * - Bloco "inteira" ocupa a linha.
 *
 * Ex.: `[economy, calendar, performance]` → `[[economy, calendar], [performance]]`;
 * `[economy, performance, calendar]` → `[[economy], [performance], [calendar]]`.
 * @param blocks Blocos ligados e que vão renderizar (já sem os que dependem de empresa/flag e não aparecem agora).
 */
export function groupHomeRows(blocks: readonly HomeBlockId[]): HomeBlockId[][] {
  const rows: HomeBlockId[][] = [];
  let index = 0;

  while (index < blocks.length) {
    const current = blocks[index];
    const next = blocks[index + 1];
    if (
      next !== undefined &&
      homeBlockDefinition(current).width === 'half' &&
      homeBlockDefinition(next).width === 'half'
    ) {
      rows.push([current, next]);
      index += 2;
    } else {
      rows.push([current]);
      index += 1;
    }
  }

  return rows;
}

/** Pares de blocos que dividem a linha com a mesma altura; os demais pares ficam alinhados no topo. */
const STRETCHED_HOME_PAIRS: readonly (readonly [HomeBlockId, HomeBlockId])[] = [
  // Crítica da F2: rodapés alinhados.
  ['bank-limits', 'meetings'],
  // Pedido do Pedro (30/10/2026, Task 3839): os dois cards de economia dividem a altura do calendário.
  ['economy', 'calendar'],
];

/**
 * Diz se a linha é um par que estica os dois blocos até a altura do maior, em qualquer ordem.
 * @param row Blocos da linha.
 */
export function isStretchedHomePair(row: readonly HomeBlockId[]): boolean {
  if (row.length !== 2) return false;
  return STRETCHED_HOME_PAIRS.some(([a, b]) => row.includes(a) && row.includes(b));
}

/**
 * Classe CSS da linha da Home (card 3788, Task 3839).
 *
 * - Bloco sozinho: `dashboard-management__row--single` (altura natural).
 * - Economia Prevista ao lado de outro bloco: grade 5/8 do protótipo (`--economy-last` quando ela está à direita);
 *   ao lado do Calendário estica (os dois cards dividem a altura dele), ao lado de outro bloco fica no topo (`--top`).
 * - Demais pares: duas colunas iguais; Limites + Reuniões esticam, os outros ficam no topo (`--top`).
 * @param row Blocos da linha.
 */
export function homeRowClass(row: readonly HomeBlockId[]): string {
  const base = 'dashboard-management__row';
  if (row.length === 1) return `${base} ${base}--single`;
  const stretched = isStretchedHomePair(row);
  if (row.includes('economy')) {
    const side = row[1] === 'economy' ? ` ${base}--economy-last` : '';
    return stretched ? `${base}${side}` : `${base}${side} ${base}--top`;
  }
  const cards = 'dashboard-management__cards';
  return stretched ? cards : `${cards} ${cards}--top`;
}
